/**
 * Spike-only stub for `next/navigation`. Wraps react-router-dom so
 * components that call useRouter / useSearchParams / redirect keep
 * working with their existing API shape.
 *
 * For the real migration: replace all `next/navigation` imports with
 * direct react-router-dom imports. The shapes don't match 1:1 (e.g.
 * `router.refresh()` has no react-router equivalent — would need a
 * state-bump pattern) but they're close enough that the codemod is
 * mechanical.
 */
import {
  useNavigate,
  useSearchParams as useRRSearchParams,
  useLocation,
  useParams as useRRParams,
} from "react-router-dom";

export function useRouter() {
  const navigate = useNavigate();
  return {
    push:    (href: string) => navigate(href),
    replace: (href: string) => navigate(href, { replace: true }),
    back:    () => navigate(-1),
    forward: () => navigate(1),
    // No react-router equivalent — Next's refresh() re-fetches RSC
    // payloads. In a Vite SPA the closest is a state bump that
    // re-triggers data effects. For the spike this is a no-op so
    // locale-cycle hotkey still "succeeds" without crashing.
    refresh: () => void 0,
    prefetch: () => void 0,
  };
}

export function useSearchParams() {
  const [params] = useRRSearchParams();
  return params;
}

// next/navigation's usePathname returns the current pathname as a
// string. react-router's useLocation gives us the same value.
export function usePathname(): string {
  return useLocation().pathname;
}

// next/navigation's useParams returns the dynamic route params as a
// plain object. react-router-dom's useParams matches this shape;
// re-export under the same name.
export const useParams = useRRParams;

// `redirect` is server-only in Next; in SPA-land, throw is wrong, but
// no component imports it at runtime for client paths. Stub as a hard
// reload — the only place it's called is the /audience/[role] server
// route, which will be ported to a Vite client route + Navigate.
export function redirect(path: string): never {
  if (typeof window !== "undefined") {
    window.location.href = path;
  }
  throw new Error(`redirect(${path}) — unreachable in SPA mode`);
}
